import React, { Dispatch, SetStateAction, useMemo, useEffect } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Drawer,
  Divider,
  IconButton,
  Typography,
  Stack,
  ListItemIcon,
  ListItemText,
  MenuItem as MuiMenuItem,
  MenuList,
  Menu,
  Tooltip,
  useTheme,
  Switch,
  CircularProgress,
} from "@mui/material";
import useDeliveryControl from "@/hooks/useDeliveryControl";
import { styled } from "@mui/material/styles";
import useOrganizations from "@/features/organizations/useOrganizations";
import { useAccessControl } from "@/features/access-control";
import Logo from "@/assets/logos/bitelink-logo-light.svg";

import { logOut } from "@/features/auth/slice";
import { useAppDispatch, useAppSelector, useResponsive, useTerminology } from "@/hooks";
import { getFirstAndFirstLastName } from "@/utils/names";
import routes from "@/constants/routes";
import useSnackbar from "@/hooks/useSnackbar";
import { useTour } from "@reactour/tour";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  BubbleChatFavouriteIcon as ChatFavouriteIcon,
  Dish01Icon,
  FingerAccessIcon,
  MenuRestaurantIcon,
  MessageQuestionIcon,
  RestaurantTableIcon,
  Setting07Icon,
  WebValidationIcon,
  UserCircleIcon,
  CancelSquareIcon,
  BookEditIcon,
  Chart03Icon,
  Store03Icon,
  Diamond02Icon,
  MenuCollapseIcon,
  Menu01Icon,
  GiftIcon,
  StartUp02Icon,
} from "@hugeicons/core-free-icons";
import OrganizationSelector from "@/features/admin-layout/components/OrganizationSelector";


interface Props {
  isSidebarExpanded: boolean;
  setIsSidebarExpanded: Dispatch<SetStateAction<boolean>>;
}

interface SidebarMenuOption {
  label: string;
  slug: string;
  icon: React.ReactNode;
  divider?: boolean;
  hidden: boolean;
  order: number;
  dataTour?: string;
  url?: string;
}

const MenuItem = styled(MuiMenuItem)({
  "&": {
    paddingTop: 12,
    paddingBottom: 12,
    border: "1px solid transparent",
    borderRadius: 8,

    "&:hover": {
      backgroundColor: "#ffffff10",
    },
  },
  "&.Mui-selected": {
    borderColor: "#ffffff50",
    backgroundColor: "transparent",

    ".MuiListItemText-root": {
      opacity: 1,
    },
  },
  "& svg": {
    stroke: "white",
    strokeWidth: 1.5,
  },
  ".MuiListItemText-root": {
    opacity: 0.5,
  },
});


const Sidebar = ({ isSidebarExpanded, setIsSidebarExpanded }: Props) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const [openSnackbar] = useSnackbar();
  const { isMobile } = useResponsive();
  const { hasAccess } = useAccessControl();
  const { organizationId } = useParams<{ organizationId: string }>();
  const { organizations, currentOrganization } = useOrganizations();
  const profile = useAppSelector((state) => state.auth.user);
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);
  const { setIsOpen } = useTour();
  const { t } = useTerminology();
  const { isDeliveryEnabled, toggleDelivery, isLoading: isDeliveryLoading, scope: deliveryScope } = useDeliveryControl();

  const isActive = (path: string) => {
    const pathLength = path.split("/").filter((p) => p !== "").length;
    const urlPaths = pathname.split("/").filter((p) => p !== "");
    const currentPath = urlPaths.slice(urlPaths.length - pathLength, urlPaths.length).join("/");
    return currentPath === path;
  };

  const sidebarMenu = useMemo(() => {
    const menuItems: SidebarMenuOption[] = [
      {
        label: "Primeros Pasos",
        slug: "primeros-pasos",
        icon: <HugeiconsIcon icon={StartUp02Icon} color="white" />,
        divider: true,
        hidden: !currentOrganization,
        order: 0,
      },
      {
        label: "Dashboard",
        slug: "dashboard",
        icon: <HugeiconsIcon icon={Chart03Icon} color="white" />,
        divider: true,
        hidden: !hasAccess("view.dashboard") || !currentOrganization,
        order: 1,
      },
      {
        label: "Pedidos",
        slug: "pedidos",
        icon: <HugeiconsIcon icon={Dish01Icon} color="white" />,
        divider: true,
        hidden: !hasAccess("view.order") || !currentOrganization,
        order: 2,
      },
      {
        label: "Contenido",
        slug: "contenido/publicados",
        icon: <HugeiconsIcon icon={BookEditIcon} color="white" />,
        hidden: !hasAccess("view.content") || !currentOrganization,
        order: 4,
        dataTour: "organization-content",
      },
      {
        label: "Fidelización",
        slug: "loyalty",
        icon: <HugeiconsIcon icon={GiftIcon} color="white" />,
        hidden: !currentOrganization?.settings?.loyaltyProgram?.isEnabled || !currentOrganization,
        order: 3,
      },
      {
        label: "FAQs",
        slug: "faqs",
        icon: <HugeiconsIcon icon={MessageQuestionIcon} color="white" />,
        hidden: !hasAccess("view.faq") || !currentOrganization,
        order: 6,
      },
      {
        label: "Comercio",
        slug: "detalles",
        icon: <HugeiconsIcon icon={Store03Icon} color="white" />,
        hidden: !hasAccess("view.organization") || !currentOrganization,
        order: 7,
        dataTour: "organization-details",
      },
      {
        label: "Accesos",
        slug: "accesos",
        icon: <HugeiconsIcon icon={FingerAccessIcon} color="white" />,
        hidden: !hasAccess("create.access") || !currentOrganization,
        order: 9,
      },
      {
        label: "Sitio Web",
        slug: "websites",
        icon: <HugeiconsIcon icon={WebValidationIcon} color="white" />,
        hidden: true,
        order: 10,
      },
      {
        label: "Sitio Web",
        slug: "mi-sitio",
        icon: <HugeiconsIcon icon={WebValidationIcon} color="white" />,
        hidden: !hasAccess("view.website") || !currentOrganization,
        order: 10,
      },
      {
        label: "Encuestas",
        slug: "surveys",
        icon: <HugeiconsIcon icon={ChatFavouriteIcon} color="white" />,
        hidden: profile?.role !== "super-admin" || !currentOrganization,
        order: 11,
      },
      {
        label: "Configuraciones",
        slug: "configuraciones",
        icon: <HugeiconsIcon icon={Setting07Icon} color="white" />,
        hidden: !hasAccess("update.organization") || !currentOrganization,
        order: 12,
        dataTour: "organization-settings",
      },
    ];

    const isFood = currentOrganization?.industry === "food-and-beverages";
    const isOther = currentOrganization?.industry === "other";

    if (isFood) {
      menuItems.push({
        label: "Mesas",
        slug: "mesas",
        icon: <HugeiconsIcon icon={RestaurantTableIcon} color="white" />,
        hidden: !hasAccess("view.table") || !currentOrganization,
        order: 6,
      });
    }

    if (isFood || isOther) {
      menuItems.push(
        {
          label: t("catalog", 2, true),
          slug: "menus",
          icon: <HugeiconsIcon icon={MenuRestaurantIcon} color="white" />,
          hidden: !hasAccess("view.menu") || !currentOrganization,
          order: 5,
          dataTour: "organization-menu",
        },
        {
          label: "Suscripción",
          slug: "suscripcion",
          icon: <HugeiconsIcon icon={Diamond02Icon} color="white" />,
          hidden: profile?.role !== "super-admin" || !currentOrganization,
          order: 15,
        },
      );
    }

    return menuItems
      .filter(({ hidden }) => !hidden)
      .sort((a, b) => {
        if (a.order < b.order) return -1;
        if (a.order > b.order) return +1;
        return 0;
      });
  }, [
    currentOrganization?.industry,
    currentOrganization?.settings?.loyaltyProgram?.isEnabled,
    profile?.role,
    hasAccess,
    t,
  ]);

  useEffect(() => {
    const appSettings = JSON.parse(localStorage.getItem("bl") ?? "{}");

    if (!appSettings?.tourAdminLayout) {
      setIsOpen(true);
    }

    appSettings["tourAdminLayout"] = true;
    window.localStorage.setItem("bl", JSON.stringify(appSettings));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userSettings = useMemo(
    () => {

      const defaultSettings = [
        {
          label: "Perfil",
          fn: () => navigate(`/comercios/${organizationId}/perfil`),
        },
        {
          label: "Cerrar Sesión",
          fn: () => dispatch(logOut()),
        },
      ];

      if (profile?.role !== "super-admin") return defaultSettings;

      const isSuperAdminMenuHidden = localStorage.getItem("hideSuperAdminMenu") === "true";

      if (isSuperAdminMenuHidden) return defaultSettings;

      return [
        {
          label: "Clientes",
          fn: () => navigate(`/super-admin/clients`),
        },
        {
          label: "Nuevo cliente",
          fn: () => navigate(`/super-admin/clients/new`),
        },
        {
          label: "Tutoriales",
          fn: () => navigate(`/super-admin/learning-center`),
        },
        {
          label: "Plantillas",
          fn: () => navigate(`/super-admin/plantillas`),
        },
        {
          type: "divider",
        },
        ...defaultSettings
      ];
    }, [openSnackbar, profile?.email],
  );

  const width = useMemo(() => {
    if (isMobile) {
      return isSidebarExpanded ? "100%" : 0;
    }
    return isSidebarExpanded ? 300 : 100;
  }, [isSidebarExpanded, isMobile]);

  return (
    <Drawer
      open={isMobile ? isSidebarExpanded : true}
      sx={{
        width,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width,
          boxSizing: "border-box",
          backgroundColor: "#06175D",
          color: "white",
        },
      }}
      variant={isMobile ? "persistent" : "permanent"}
      anchor="left"
    >
      <Stack py={3} px={2} direction="row" justifyContent={isSidebarExpanded ? "space-between" : "center"} alignItems="center">
        {isSidebarExpanded && (
          <Link to={routes.organizations.main}>
            <img src={Logo} alt="BiteLink" style={{ height: "43px" }} />
          </Link>
        )}
        {isMobile ? (
          <IconButton onClick={() => setIsSidebarExpanded(false)}>
            <HugeiconsIcon icon={CancelSquareIcon} color="white" size={28} />
          </IconButton>
        ) : (
          <IconButton onClick={() => setIsSidebarExpanded(!isSidebarExpanded)}>
            <HugeiconsIcon icon={isSidebarExpanded ? MenuCollapseIcon : Menu01Icon} color="white" size={28} />
          </IconButton>
        )}
      </Stack>
      {!isSidebarExpanded && !isMobile && <Divider color={"#1C2C6B"} sx={{ mx: 2 }} />}

      {organizationId && isSidebarExpanded ? (
        <Box px={2}>
          <OrganizationSelector
            organizations={organizations}
            onCreateOrganization={() => navigate(routes.organizations.create)}
            onNavigate={(newOrgId) => {
              const currentPath = window.location.pathname;
              const newPath = currentPath.replace(organizationId as string, newOrgId);
              const queryParams = window.location.search;
              navigate(newPath + queryParams);
            }}
          />
        </Box>
      ) : null}

      <MenuList sx={{ paddingX: 2, flex: 1 }}>
        {sidebarMenu.map((menu) => (
          <Tooltip
            key={menu.slug}
            title={menu.label}
            placement="right"
            disableHoverListener={isSidebarExpanded || isMobile}
            arrow
          >
            <MenuItem
              selected={isActive(menu.slug)}
              onClick={() => {
                if (menu?.url) {
                  navigate(menu.url);
                } else {
                  navigate(`/comercios/${organizationId}/${menu.slug}`);
                  if (isMobile) {
                    setIsSidebarExpanded(false);
                  }
                }
              }}
              data-tour={menu?.dataTour}
              sx={{ justifyContent: isSidebarExpanded ? "flex-start" : "center" }}
            >
              <ListItemIcon sx={{ minWidth: isSidebarExpanded ? 40 : 0, justifyContent: "center" }}>{menu.icon}</ListItemIcon>
              {isSidebarExpanded && <ListItemText>{menu.label}</ListItemText>}
            </MenuItem>
          </Tooltip>
        ))}
      </MenuList>

      <Stack spacing={1} p={2}>
        <Divider color={"#1C2C6B"} />
        {isSidebarExpanded ? (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            px={2}
            py={1}
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.08)",
              borderRadius: 2,
              border: "1px solid rgba(255, 255, 255, 0.15)",
              backdropFilter: "blur(20px)",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: isDeliveryEnabled ? "#22c55e" : "#ef4444",
                  boxShadow: isDeliveryEnabled ? "0 0 8px #22c55e" : "none",
                }}
              />
              <Typography variant="body2" sx={{ color: "white", fontWeight: 500 }}>
                Delivery: {isDeliveryEnabled ? "Activo" : "Inactivo"}
              </Typography>
            </Stack>
            {isDeliveryLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <Switch
                checked={isDeliveryEnabled}
                onChange={() => toggleDelivery()}
                size="small"
                color="primary"
              />
            )}
          </Stack>
        ) : (
          <Tooltip title={`Delivery: ${isDeliveryEnabled ? "Activo" : "Inactivo"}`} placement="right" arrow>
            <Stack
              alignItems="center"
              justifyContent="center"
              py={1}
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.08)",
                borderRadius: 2,
                border: "1px solid rgba(255, 255, 255, 0.15)",
                backdropFilter: "blur(20px)",
              }}
            >
              {isDeliveryLoading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <Switch
                  checked={isDeliveryEnabled}
                  onChange={() => toggleDelivery()}
                  size="small"
                  color="primary"
                />
              )}
            </Stack>
          </Tooltip>
        )}
        <Stack
          padding={1}
          direction={"row"}
          alignItems={"center"}
          justifyContent={isSidebarExpanded ? "space-between" : "center"}
        >
          {isSidebarExpanded && (
            <Typography>
              {getFirstAndFirstLastName(profile?.name, profile?.lastname)}
            </Typography>
          )}
          <IconButton onClick={(event: React.MouseEvent<HTMLElement>) => setAnchorElUser(event.currentTarget)} data-tour="user-menu">
            <HugeiconsIcon icon={UserCircleIcon} color="white" />
          </IconButton>
        </Stack>
      </Stack>
      <Menu
        sx={{ mt: "45px" }}
        id="menu-appbar"
        anchorEl={anchorElUser}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        keepMounted
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        open={Boolean(anchorElUser)}
        onClose={() => setAnchorElUser(null)}
        MenuListProps={{
          sx: { py: 0.5 }
        }}
      >
        {userSettings.map((menuItem, index) =>
          menuItem.type === "divider" ? (
            <Divider key={`${menuItem.label ?? "divider"}-${index}`} >
              <Typography variant="caption">{menuItem.label}</Typography>
            </Divider>
          ) : (
            <MenuItem
              dense
              key={`${menuItem.label ?? "menu-item"}-${index}`}
              onClick={menuItem.fn ?? undefined}
            >
              <Typography variant="body2">{menuItem.label}</Typography>
            </MenuItem>
          )
        )}
      </Menu>
    </Drawer>
  );
};

export default Sidebar;
